import React, { useState, useEffect, useMemo } from 'react';
import Accordion from '../common/Accordion';
import { sortByAlpha } from '../../helpers/sorters';
import ContainerPermission from './ContainerPermission';
import {
  createContainerRole,
  updateContainerRole,
  destroyContainerRole,
} from './constants/handleRoles';
import { fetchUsers } from './constants/handleUsers';
import Icon from '../common/Icon';

const UserRoles = ({ roles, setRoles, container_id }) => {
  const [users, setUsers] = useState([]);
  const [testUsers, setTestUsers] = useState([]);
  const [usersDidFetch, setUsersDidFetch] = useState(false);
  const [showUserRoles, setShowUserRoles] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const toggleShowUserRoles = () => setShowUserRoles(!showUserRoles);
  const userIdsByRole = roles.map((r) => r.user_id);
  const filteredUsers = users.filter(
    (u) => !u.admin && !userIdsByRole.includes(u.id)
  );
  const externalIdsByRole = roles.map((r) => r.external_user_id);
  const filteredExternals = testUsers.filter(
    (e) => !e.admin && !e.internal_link && !externalIdsByRole.includes(e.id)
  );

  useEffect(() => {
    if (showUserRoles && !usersDidFetch)
      fetchUsers().then(({ users, externals }) => {
        if (users.ok) setUsers(sortByAlpha(users.data, 'name'));
        if (externals.ok) setTestUsers(sortByAlpha(externals.data, 'name'));
        setUsersDidFetch(true);
      });
  }, [showUserRoles]);

  const handleCreateRole = async () => {
    if (!selectedUser) return;
    let [id, model] = selectedUser.split('-');
    let role = { [model + '_id']: id, role: 0, container_id };
    let created = await createContainerRole(role);
    if (created.ok) {
      setRoles([...roles, created.data]);
      setSelectedUser('');
    }
  };

  const handleUpdateRole = async (id, permission) => {
    let role = { role: permission };
    let updated = await updateContainerRole(id, role);
    if (updated.ok)
      setRoles([...roles.filter((r) => r.id != id), updated.data]);
  };

  const handleDestroyRole = async (id) => {
    let deleted = await destroyContainerRole(id);
    if (deleted.ok) setRoles(roles.filter((r) => r.id != id));
  };

  const userRoles = useMemo(
    () =>
      roles
        .filter((r) => r.user)
        .sort((a, b) => a.user.name.localeCompare(b.user.name))
        .map((role, index) => {
          return (
            <li key={`role-${role.id}`}>
              <ContainerPermission
                role={role}
                modelName={'user'}
                updateRole={handleUpdateRole}
                destroyRole={handleDestroyRole}
                includeUserName
              />
            </li>
          );
        }),
    [roles]
  );

  const externalRoles = useMemo(
    () =>
      roles
        .filter((r) => r.external_user)
        .sort((a, b) =>
          a.external_user.name.localeCompare(b.external_user.name)
        )
        .map((role) => (
          <li key={`role-${role.id}`}>
            <ContainerPermission
              role={role}
              modelName={'external_user'}
              updateRole={handleUpdateRole}
              destroyRole={handleDestroyRole}
              includeUserName
            />
          </li>
        )),
    [roles]
  );

  return (
    <section className='user-roles'>
      <h2>Users</h2>

      <Accordion
        expanded={showUserRoles}
        toggle={toggleShowUserRoles}
        id='user-role-dropdown'
      >
        {usersDidFetch ? (
          <div>
            <fieldset>
              <div className='row'>
                <div className='third'>
                  <div className='select-input'>
                    <select
                      onChange={(e) => setSelectedUser(e.target.value)}
                      value={selectedUser}
                    >
                      <option label='Select User' value={''} />
                      <optgroup label='Users'>
                        {filteredUsers.map((user) => (
                          <option
                            key={'user-' + user.id}
                            label={user.name}
                            value={user.id + '-user'}
                          />
                        ))}
                      </optgroup>
                      <optgroup label='Test'>
                        {filteredExternals.map((ex) => (
                          <option
                            key={'external-' + ex.id}
                            label={ex.name}
                            value={ex.id + '-external_user'}
                          />
                        ))}
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div className='quarter'>
                  <button onClick={handleCreateRole}>
                    Add <Icon icon='plus' />
                  </button>
                </div>
              </div>
            </fieldset>

            <h3>User Permissions</h3>
            <ul>
              {userRoles.length > 0 ? (
                userRoles
              ) : (
                <li>No roles have been added to this container</li>
              )}
            </ul>
            {externalRoles.length > 0 ? (
              <>
                <h3>Test</h3>
                <ul>{externalRoles}</ul>
              </>
            ) : null}
          </div>
        ) : (
          <>Loading...</>
        )}
      </Accordion>
    </section>
  );
};

export default UserRoles;
